import React, { FC, memo, useEffect } from 'react';
import { UitkExpandoList } from 'uitk-react-expando';
import { SectionFragment, useSidebarQuery } from '../../graphql/__generated__/types';
import { useBexApi } from '@shared-ui/bex-api-context';
import useNavigationParams from '../../hooks/useNavigationParams';
import SidebarSection from './sidebar-section/sidebar-section';
import useGraphqlErrors from '../../hooks/useGraphqlErrors';
import ProductPlaceHolder from '../product-placeholder';
import LoadingSkeleton from './loading-skeleton';
import { UitkSpacing } from 'uitk-react-spacing';
import useSideBarVisibility from '../../hooks/useSideBarVisiblity';
import useMessageBanner from '../../hooks/useMessageBanner';

const Sidebar: FC = () => {
  const { context } = useBexApi();
  const { hubId, tabId, documentId } = useNavigationParams();
  const { setHasError } = useGraphqlErrors();
  const { setHideSideBar } = useSideBarVisibility();
  const { setMessageBanner } = useMessageBanner();
  // TODO - get spaces from BE (either header or a new query)
  const spaces = ['products', 'discovery', 'control-plane'];
  const isProducts = spaces.includes(tabId);
  const parentId = isProducts ? `${tabId}/${documentId.split('/')?.[1]}` : tabId;
  const { data, error, loading } = useSidebarQuery({
    variables: { hubId, parentId, context },
  });

  useEffect(() => {
    if (error) setHasError(true);
  }, [error]);

  React.useEffect(() => {
    if (data) {
      if (data.documentation?.navigation?.elements.length) {
        setHideSideBar(false);
      } else {
        setHideSideBar(true);
      }
      if (data.documentation?.navigation?.notifications !== null) {
        setMessageBanner({
          message: data.documentation?.navigation?.notifications[0]?.message,
          iconName: data.documentation?.navigation?.notifications[0]?.icon.token,
          messageStyle: { marginLeft: '1rem' },
        });
      }
    }
  }, [data]);

  if (loading || !data) {
    return (
      <UitkSpacing margin={{ block: 'three' }}>
        <div>
          <LoadingSkeleton />
        </div>
      </UitkSpacing>
    );
  }

  if (!data.documentation.navigation.elements.length) {
    return null;
  }

  const sections = data.documentation.navigation.elements;
  const {
    title: { text },
  } = data.documentation.navigation.heading;

  return (
    <div className="sidebar-content">
      <UitkExpandoList data-testid="sidebar">
        <ProductPlaceHolder label={text} />
        <SidebarSections sections={sections} />
      </UitkExpandoList>
    </div>
  );
};

const SidebarSections: FC<{ sections: Array<SectionFragment> }> = ({ sections }) => (
  <div data-testid="sidebar-short-title">
    {sections.map((section, index) => (
      <SidebarSection key={index} section={section} withBottomBorder={index !== sections.length - 1} />
    ))}
  </div>
);

export default memo(Sidebar);
